import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  makeStyles,
  Typography
} from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { isEmpty } from 'lodash';
import { useObserver } from 'mobx-react-lite';
import React, { Fragment, useState, FC, useEffect } from 'react';
import { ViewState } from '../../../domain/models/ViewState';
import OrderInfo from './OrderInfo/OrderInfo';
import PaymentInfo from './PaymentInfo/PaymentInfo';
import Submit from './Submit/Submit';
import Thanks from '../Thanks/Thanks';
import NoDriverDelivery from './NoDriverDelivery/NoDriverDelivery';
import { LayoutVariant } from '../../Core/CommonTypes';
import { OnlineOrderingStore } from 'kiolyn-api';
import { useOrdering } from '../../../stores/OrderingStore';
import SubmitSteps from './SubmitSteps/SubmitSteps';
import { OnlineOrderType } from '../../../domain/models/CommonTypes';
import TipGuides from './TipGuides/TipGuides';
import FootNotes from './FootNotes/FootNotes';
import { isNullOrUndefined } from 'util';
import { ValidationErrors } from '../../Core/Validators';
import Waiting from '../Waiting/Waiting';
import { SaveOrderAsyncViewState } from '../../../domain/models/SaveOrderViewState';
import QrCodeApplePay from './PaymentInfo/QrCodeApplePay';

const useStyles = makeStyles(theme => ({
  container: {
    height: '100%',
    width: '100%'
  },
  paper: {
    height: 'calc(100% - 96px)',
    minWidth: 480
  },
  title: {
    marginRight: 48
  },
  close: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1)
  },
  dialogContent: {},
  mobileDialogContent: {
    padding: theme.spacing(2),
    paddingTop: 0
  }
}));

interface SubmitOnlineOrderProps {
  variant?: LayoutVariant;
  onClose?: () => void;
  tableId?: string;
}

const SubmitOnlineOrder: FC<SubmitOnlineOrderProps> = ({
  onClose,
  variant,
  tableId
}) => {
  const classes = useStyles();
  const orderingStore = useOrdering();

  const store = orderingStore.store as OnlineOrderingStore;
  const order = orderingStore.order;
  const acceptPayment = store.accept_online_payment ?? false;
  const acceptTip = store.tip_settings?.enable_tip_guides ?? false;
  const acceptFootnotes = store.footnotes_settings?.enable_footnotes ?? true;
  const contactlessDelivery = !store.footnotes_settings
    ?.no_contactless_delivery;

  if (isNullOrUndefined(order.no_plastic_utensils)) {
    if (!tableId) {
      if (!acceptFootnotes) {
        order.no_plastic_utensils = false;
      } else if (
        acceptFootnotes &&
        isNullOrUndefined(store.footnotes_settings?.no_plastic_utensils)
      ) {
        order.no_plastic_utensils = true;
      } else if (
        !isNullOrUndefined(store.footnotes_settings?.no_plastic_utensils)
      ) {
        order.no_plastic_utensils =
          store.footnotes_settings?.no_plastic_utensils;
      }
    }
  }

  const acceptOrderTypes: OnlineOrderType[] = [];
  if (store.accept_delivery === true) {
    acceptOrderTypes.push('delivery');
  }
  if (store.accept_pickup === true) {
    acceptOrderTypes.push('pickup');
  }
  if (store.accept_dinein === true || tableId) {
    acceptOrderTypes.push('dinein');
  }
  if (!acceptOrderTypes.find(t => `${t}` === order.order_type)) {
    orderingStore.setOrderType(acceptOrderTypes[0]);
  }

  const [activeStep, setActiveStep] = useState(1);
  const [havingError, setHavingError] = useState(true);

  const handleHavingErrorValued = async (errors: ValidationErrors) => {
    if (order.order_type !== 'delivery') {
      if (errors['quote_id'] === 'noQuote') {
        delete errors['quote_id'];
      }
      setHavingError(!isEmpty(errors));
    } else {
      setHavingError(!isEmpty(errors));
    }
  };

  const handleUpdateCalculatedValues = async () => {
    await orderingStore.updateOrderAmounts();
  };

  const handleClose = (_?: object, reason?: string) => {
    if (!reason || reason !== 'backdropClick') {
      orderingStore?.websocket?.close();
      onClose?.();
    }
  };

  const handleBack = () => {
    orderingStore.submitState = ViewState.NONE;
    setHavingError(false);
    setActiveStep(activeStep - 1);
  };

  const handleChangeToPickupOrder = async () => {
    orderingStore.setOrderType('pickup');
    await orderingStore.submitCurrentOrder(false);
  };

  const orderInfo = (
    <OrderInfo
      key="orderInfo"
      config={{
        auto_move_send: store.auto_move_send,
        delay_delivery_time: store.delay_delivery_time,
        delay_pickup_time: store.delay_pickup_time,
        delay_dinein_time: store.delay_dinein_time,
        bizHours: store.biz_hours,
        holidays: store.holidays,
        deliverySettings: store.delivery_settings,
        acceptedOrderTypes: acceptOrderTypes
      }}
      onUpdateCalculatedValues={() => handleUpdateCalculatedValues()}
      onErrorsChange={errors => handleHavingErrorValued(errors)}
      variant={variant}
      isOrderFromTable={!!tableId}
    />
  );

  const paymentInfo = (
    <PaymentInfo
      key="paymentInfo"
      order={order}
      onErrorsChange={errors => handleHavingErrorValued(errors)}
      variant={variant}
    />
  );

  const note = order.order_type === 'delivery' ? store.tip_settings?.note : '';
  const getDefaultTipForDriver = () =>
    typeof order.default_tip_for_driver === 'number'
      ? order.default_tip_for_driver
      : store.tip_settings?.default_tip_for_driver;
  const isSplitTip =
    store.tip_settings?.split_tip_for_restaurant_staff &&
    ['postmates', 'doordash'].includes(
      store.delivery_settings?.delivery_type as string
    ) &&
    order.order_type === 'delivery';

  const tipGuides = (
    <TipGuides
      key="tip"
      total={order.subtotal}
      tipGuides={store.tip_settings?.tip_guides}
      defaultTipGuide={store.tip_settings?.default_tip_guide}
      note={note}
      noteForOnlineOrdering={store.tip_settings?.note_for_online_ordering}
      value={order.tip}
      defaultTipForDriver={getDefaultTipForDriver()}
      splitTip={isSplitTip}
      onChange={tip => {
        order.tip = tip;

        if (isSplitTip && store.tip_settings?.split_tip_for_restaurant_staff) {
          const percent = getDefaultTipForDriver() as number;
          order.tip_for_driver = tip * (percent / 100);
          order.tip_for_restaurant_staff = tip * ((100 - percent) / 100);
        }

        orderingStore.updateOrderAmounts();
      }}
      onChangeDefaultTipForDriver={defaultTipForDriver => {
        order.default_tip_for_driver = defaultTipForDriver;
      }}
    />
  );

  const footNotes = (
    <FootNotes
      key="footNotes"
      value={order.no_plastic_utensils}
      onChange={noPlasticUtensils => {
        order.no_plastic_utensils = noPlasticUtensils;
      }}
      contactlessDelivery={order.contactless_delivery ?? contactlessDelivery}
      showContactLess={order.order_type === 'delivery'}
      onChangeContactlessDelivery={checked => {
        order.contactless_delivery = checked;
      }}
      customNote={order.custom_note}
      showCustomNote={store.footnotes_settings?.custom_note === true}
      onChangeCustomNote={customNote => (order.custom_note = customNote)}
    />
  );

  const submit = (
    <Submit
      key="submit"
      confirmMessage={store.confirm_message}
      onErrorsChange={errors => handleHavingErrorValued(errors)}
    />
  );
  let steps: string[] = [];
  let contents: any[] = [];
  if (acceptPayment) {
    steps = acceptTip
      ? acceptFootnotes
        ? ['Order info', 'Tips', 'Foot Notes', 'Payment info', 'Pay & Submit']
        : ['Order info', 'Tips', 'Payment info', 'Pay & Submit']
      : acceptFootnotes
      ? ['Order info', 'Foot Notes', 'Payment info', 'Pay & Submit']
      : ['Order info', 'Payment info', 'Pay & Submit'];
    contents = acceptTip
      ? acceptFootnotes
        ? [orderInfo, tipGuides, footNotes, paymentInfo, submit]
        : [orderInfo, tipGuides, paymentInfo, submit]
      : acceptFootnotes
      ? [orderInfo, footNotes, paymentInfo, submit]
      : [orderInfo, paymentInfo, submit];
  } else {
    steps = acceptTip
      ? acceptFootnotes
        ? ['Order info', 'Tips', 'Foot Notes', 'Submit']
        : ['Order info', 'Tips', 'Submit']
      : acceptFootnotes
      ? ['Order info', 'Foot Notes', 'Submit']
      : ['Order info', 'Submit'];
    contents = acceptTip
      ? acceptFootnotes
        ? [orderInfo, tipGuides, footNotes, submit]
        : [orderInfo, tipGuides, submit]
      : acceptFootnotes
      ? [orderInfo, footNotes, submit]
      : [orderInfo, submit];
  }
  if (!!tableId) {
    steps = steps.filter(e => e !== 'Foot Notes');
    contents = contents.filter(e => e !== footNotes);
  }

  let dialogContent = (
    <Fragment>
      <SubmitSteps
        key="steps"
        activeStep={activeStep}
        steps={steps}
        variant={variant}
      />
      {contents[activeStep - 1]}
    </Fragment>
  );
  if (orderingStore.submitState === ViewState.LOADING) {
    dialogContent = (
      <Grid
        container
        className={classes.container}
        direction="column"
        alignContent="center"
        alignItems="center"
        justify="center"
      >
        <CircularProgress />
      </Grid>
    );
  } else if (
    orderingStore.submitState === SaveOrderAsyncViewState.PAYING ||
    orderingStore.submitState === SaveOrderAsyncViewState.WAITINGFAILED
  ) {
    dialogContent = <Waiting />;
  } else if (
    orderingStore.submitState === SaveOrderAsyncViewState.AWAITING_PAYMENT
  ) {
    dialogContent = <QrCodeApplePay />;
  } else if (orderingStore.submitState === ViewState.OK) {
    dialogContent = <Thanks isSendEmail={orderingStore.isSendEmail} />;
  } else if (
    orderingStore.submitState === ViewState.ERROR &&
    orderingStore.isNoDriverError
  ) {
    dialogContent = (
      <NoDriverDelivery
        onCancel={handleClose}
        onChangeToPickupOrder={handleChangeToPickupOrder}
      />
    );
  }

  useEffect(() => {
    if (orderingStore.isSelectPaymentMethod === true) {
      setActiveStep(activeStep - 1);
      orderingStore.isSelectPaymentMethod = undefined;
    }
  }, [orderingStore.isSelectPaymentMethod]); // eslint-disable-line react-hooks/exhaustive-deps

  return useObserver(() => (
    <Dialog
      onClose={handleClose}
      open
      scroll="paper"
      fullWidth={variant === LayoutVariant.laptop}
      fullScreen={variant === LayoutVariant.mobile}
      maxWidth="md"
      disableBackdropClick={orderingStore.submitState === ViewState.LOADING}
      classes={variant === LayoutVariant.mobile ? {} : { paper: classes.paper }}
    >
      <DialogTitle disableTypography>
        <Typography variant="h6" className={classes.title}>
          Submit your order
        </Typography>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          className={classes.close}
          disabled={
            orderingStore.submitState === ViewState.LOADING ||
            orderingStore.submitState === SaveOrderAsyncViewState.PAYING
          }
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        classes={{
          root:
            variant === LayoutVariant.mobile
              ? classes.mobileDialogContent
              : classes.dialogContent
        }}
      >
        {dialogContent}
      </DialogContent>
      {![
        ViewState.OK,
        SaveOrderAsyncViewState.PAYING,
        SaveOrderAsyncViewState.WAITINGFAILED,
        SaveOrderAsyncViewState.AWAITING_PAYMENT
      ].includes(orderingStore.submitState) &&
        !orderingStore.isNoDriverError && (
          <DialogActions>
            <Button
              variant="contained"
              disabled={
                activeStep === 1 ||
                orderingStore.submitState === ViewState.LOADING
              }
              onClick={() => {
                handleBack();
              }}
              color="primary"
            >
              Back
            </Button>
            {activeStep !== steps.length &&
              !!tableId &&
              steps[activeStep - 1] === 'Payment info' && (
                <Button
                  variant="contained"
                  disabled={orderingStore.submitState === ViewState.LOADING}
                  onClick={() => {
                    setActiveStep(activeStep + 1);
                    setHavingError(false);
                  }}
                  color="primary"
                >
                  Pay Later
                </Button>
              )}
            {activeStep !== steps.length && (
              <Button
                variant="contained"
                disabled={
                  havingError ||
                  orderingStore.submitState === ViewState.LOADING ||
                  orderingStore.deliveryQuoteState === ViewState.LOADING
                }
                onClick={() => setActiveStep(activeStep + 1)}
                color="primary"
              >
                Next
              </Button>
            )}
            {activeStep === steps.length && (
              <Button
                variant="contained"
                color="primary"
                aria-label="pay submit order"
                onClick={() => orderingStore.submitCurrentOrder(false, tableId)}
                disabled={
                  orderingStore.submitToken.length === 0 ||
                  orderingStore.submitState === ViewState.LOADING ||
                  havingError
                }
              >
                Submit Order
              </Button>
            )}
          </DialogActions>
        )}
    </Dialog>
  ));
};

export default SubmitOnlineOrder;
